import { KULEWEBVIEW } from '@/config/constant'
import TGASDK from '@/config/TGASDK'
import { getLocalLang, setLocalLang } from '@/i18n'

/**
 * 从当前 URL 中获取 appCode
 * @returns appCode
 */
const getAppCodeFromUrl = () => {
  const pathname = new URL(window.location.href).pathname
  const segments = pathname.split('/').filter(Boolean)

  // 兼容旧版本的 URL 格式，khalaspay渠道
  // TODO: 后续需要删除
  if (segments.length >= 2 && segments[1] === 'tiptop') {
    return segments[1]
  }

  return segments.length > 0 ? segments[0] : undefined
}

const getClient = (width?: number) => {
  return (width ? width : document.body.clientWidth) < 640 ? 'mobile' : 'pc'
}

const getBaseAppParams = async () => {
  const { lang, theme, appCode } = KULEWEBVIEW
    ? await getBaseAppParamsFromSDK()
    : { lang: getLocalLang(), theme: '', appCode: getAppCodeFromUrl() || TGASDK.DEFAULT_APP_CODE }

  // 语言设置到本地
  lang && setLocalLang(lang)

  return {
    lang,
    theme,
    appCode
  }
}

const getBaseAppParamsFromSDK = async () => {
  const res = await window.Bridge.system.gameCenterAppParam()
  if (!res.appCode) {
    const { appCode } = await window.Bridge.system.getAppConfig()

    return {
      ...res,
      appCode
    }
  }

  return res
}

const isChannel = (code: string) => {
  return TGASDK.context.appConfig.appCode === code
}

const isBip = () => {
  return TGASDK.context.appConfig.appCode === 'bip' || TGASDK.context.appConfig.appCode === 'bip-dev'
}

const addParamsToUrl = (url: string, params: { [key: string]: string }) => {
  try {
    const urlObj = new URL(url)
    const searchParams = urlObj.searchParams

    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        const value = params[key]
        searchParams.append(key, value)
      }
    }

    return urlObj.toString()
  } catch (error) {
    return url
  }
}

const getFileNameFromUrl = (url?: string): string => {
  if (!url) {
    return ''
  }

  const arr = url.split('/') || []
  return arr[arr.length - 1].split('.')[0]
}

const east8TimestampToLocal = (east8Timestamp: number): number => {
  const east8Date = new Date(east8Timestamp)
  const timeDifferenceInMilliseconds = (-8 * 60 - new Date().getTimezoneOffset()) * 60 * 1000
  const localTimestamp = east8Date.getTime() - timeDifferenceInMilliseconds
  return localTimestamp
}

const east8TimestampToDateTime = (east8Timestamp: number): string => {
  const date = new Date(east8TimestampToLocal(east8Timestamp))
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

const timeRemaining = (utcTimestamp: number, expireSeconds: number): number => {
  const timestamp = east8TimestampToLocal(utcTimestamp)
  const currentTimestamp = new Date().getTime()
  const expirationTime = timestamp + expireSeconds * 1000
  const remainingTime = expirationTime - currentTimestamp

  return remainingTime <= 0 ? 0 : Math.floor(remainingTime / 1000)
}

const coverUrlToFit = (url: string, size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl'): string => {
  const newUrl = url.replace(/_\d+x\d+\.(jpg|png|webp)/, '.$1')

  if (['xs', 'sm'].indexOf(size) >= 0) {
    return newUrl.replace(/\.(jpg|png|webp)$/, '_640x320.$1')
  }
  return newUrl
}

const generateUniqueId = (): string => {
  const timestamp = Date.now().toString()
  const randomNum = Math.floor(Math.random() * 1000000)
    .toString()
    .padStart(6, '0')

  return timestamp.slice(-10) + randomNum.slice(-6)
}

const filterObject = <T extends object>(
  obj: T,
  predicate: <K extends keyof T>(value: T[K], key: K) => boolean
): { [K in keyof T]?: T[K] } => {
  const result: { [K in keyof T]?: T[K] } = {}
  ;(Object.keys(obj) as Array<keyof T>).forEach(name => {
    if (predicate(obj[name], name)) {
      result[name] = obj[name]
    }
  })
  return result
}

const getRandomFromArray = <T>(array: T[], max: number = 6) => {
  if (array.length <= max) {
    return [...array]
  }

  const shuffled = array.slice().sort(() => Math.random() - 0.5)
  return shuffled.slice(0, max)
}

export {
  getAppCodeFromUrl,
  getClient,
  getBaseAppParams,
  isChannel,
  isBip,
  addParamsToUrl,
  getFileNameFromUrl,
  east8TimestampToDateTime,
  timeRemaining,
  coverUrlToFit,
  generateUniqueId,
  filterObject,
  getRandomFromArray
}
